// PWA Service Worker 및 Push Notification 관리
export interface NotificationPayload {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: any;
  actions?: Array<{
    action: string;
    title: string;
  }>;
}

export interface PushSubscriptionData {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

class PWAService {
  private swRegistration: ServiceWorkerRegistration | null = null;
  private isSupported: boolean = false;

  constructor() {
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
  }

  // Service Worker 등록 (Firebase Service Worker 사용)
  async registerServiceWorker(): Promise<boolean> {
    if (!this.isSupported) {
      console.warn('Service Worker 또는 Push API가 지원되지 않습니다.');
      return false;
    }

    try {
      const { registerServiceWorker } = await import('../utils/pushNotifications');
      this.swRegistration = await registerServiceWorker();
      console.log('Firebase Service Worker 등록 성공:', this.swRegistration);

      // 업데이트 확인 (새로고침 방지 및 구독 복원)
      this.swRegistration.addEventListener('updatefound', () => {
        console.log('Service Worker 업데이트 발견');
        const newWorker = this.swRegistration?.installing;
        
        if (newWorker) {
          newWorker.addEventListener('statechange', async () => {
            if ((newWorker as ServiceWorker).state === 'installed' && navigator.serviceWorker.controller) {
              console.log('새 Service Worker 설치 완료 - 푸시 구독 보존을 위해 새로고침 스킵');
              // 즉시 활성화 요청 (새로고침 없이)
              newWorker.postMessage({ type: 'SKIP_WAITING' });
              
              // Service Worker 업데이트 후 푸시 구독 복원 시도
              try {
                // 로그인 상태 확인
                const token = localStorage.getItem('token');
                if (token) {
                  console.log('🔄 [Service Worker 업데이트] 로그인 상태 확인 - 푸시 구독 복원 시도');
                  
                  // 동적 import로 순환 참조 방지
                  const pushNotificationService = (await import('./pushNotificationService')).default;
                  
                  // Service Worker가 완전히 활성화될 때까지 대기
                  await new Promise(resolve => setTimeout(resolve, 1000));
                  
                  // 구독 상태 확인 및 복원
                  await pushNotificationService.checkAndRestoreSubscription();
                  console.log('✅ [Service Worker 업데이트] 푸시 구독 복원 완료');
                } else {
                  console.log('ℹ️ [Service Worker 업데이트] 로그인 상태 아님 - 구독 복원 스킵');
                }
              } catch (error) {
                console.error('❌ [Service Worker 업데이트] 푸시 구독 복원 실패:', error);
                // 실패해도 앱은 정상 동작 (로그인 후 자동 복원됨)
              }
            }
          });
        }
      });

      return true;
    } catch (error) {
      console.error('Service Worker 등록 실패:', error);
      return false;
    }
  }

  // Push 알림 권한 요청
  async requestNotificationPermission(): Promise<NotificationPermission> {
    if (!this.isSupported) {
      return 'denied';
    }

    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      console.log('알림 권한:', permission);
      return permission;
    }

    return 'denied';
  }

  // Push 구독 생성
  async subscribeToPush(): Promise<PushSubscriptionData | null> {
    if (!this.swRegistration) {
      console.error('Service Worker가 등록되지 않았습니다.');
      return null;
    }

    try {
      const permission = await this.requestNotificationPermission();
      if (permission !== 'granted') {
        console.warn('알림 권한이 거부되었습니다.');
        return null;
      }

      // VAPID 공개 키 (Firebase 설정과 동일하게 통일)
      const vapidPublicKey = 'BKhE-ykBuZY26rLp8R1OO6KtQaczwTXAGYbfM-MfeLdkgV9I1gdeP5AxfNIUgdsmzlLKuvp8ihS12FI8GgirUNQ';
      
      const applicationServerKey = this.urlBase64ToUint8Array(vapidPublicKey);

      const subscription = await this.swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey
      });

      const subscriptionData: PushSubscriptionData = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')!),
          auth: this.arrayBufferToBase64(subscription.getKey('auth')!)
        }
      };

      console.log('Push 구독 생성 성공:', subscriptionData);
      return subscriptionData;
    } catch (error) {
      console.error('Push 구독 생성 실패:', error);
      return null;
    }
  }

  // Push 구독 해제
  async unsubscribeFromPush(): Promise<boolean> {
    if (!this.swRegistration) {
      return false;
    }

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
        console.log('Push 구독 해제 성공');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Push 구독 해제 실패:', error);
      return false;
    }
  }

  // 로컬 알림 표시
  async showLocalNotification(payload: NotificationPayload): Promise<void> {
    if (!this.isSupported || !this.swRegistration) {
      console.warn('알림을 표시할 수 없습니다.');
      return;
    }

    const permission = await this.requestNotificationPermission();
    if (permission !== 'granted') {
      console.warn('알림 권한이 없습니다.');
      return;
    }

    const options: any = {
      body: payload.body,
      icon: payload.icon || '/icon-192x192.png',
      badge: payload.badge || '/icon-72x72.png',
      tag: payload.tag || 'judam-notification',
      data: payload.data,
      requireInteraction: true,
      vibrate: [200, 100, 200],
      actions: payload.actions
    };

    await this.swRegistration.showNotification(payload.title, options);
  }

  // 앱 업데이트 알림
  private showUpdateNotification(): void {
    this.showLocalNotification({
      title: '주담 클라우드 업데이트',
      body: '새로운 버전이 사용 가능합니다. 새로고침하여 업데이트하세요.',
      tag: 'app-update',
      actions: [
        { action: 'reload', title: '새로고침' },
        { action: 'dismiss', title: '나중에' }
      ]
    });
  }

  // Service Worker와 메시지 통신
  sendMessageToSW(message: any): void {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage(message);
    }
  }

  // 앱 설치 가능 여부 확인
  isInstallable(): boolean {
    return 'beforeinstallprompt' in window;
  }

  // 백그라운드 동기화 등록
  async registerBackgroundSync(tag: string): Promise<void> {
    if (!this.swRegistration || !('sync' in this.swRegistration)) {
      console.warn('Background Sync가 지원되지 않습니다.');
      return;
    }

    try {
      await (this.swRegistration as any).sync.register(tag);
      console.log('Background Sync 등록 성공:', tag);
    } catch (error) {
      console.error('Background Sync 등록 실패:', error);
    }
  }

  // 유틸리티 함수들
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  // PWA 상태 확인
  getStatus(): {
    isSupported: boolean;
    isRegistered: boolean;
    isInstalled: boolean;
  } {
    return {
      isSupported: this.isSupported,
      isRegistered: !!this.swRegistration,
      isInstalled: window.matchMedia('(display-mode: standalone)').matches
    };
  }

  // 캐시 정리
  async clearCaches(): Promise<void> {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('모든 캐시가 정리되었습니다.');
    }
  }
}

// 싱글톤 인스턴스
const pwaService = new PWAService();
export default pwaService;

// 전역에서 사용할 수 있도록 타입 선언
declare global {
  interface Window {
    beforeinstallprompt?: any;
  }
} 